import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import {
  Avatar,
  Box,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useDashboardContext } from 'context/dashboard.context';
import Weighted from 'components/Elements/Typography/Weighted.styled';
import { Link } from 'react-router-dom';
import { KeyboardArrowDown, KeyboardArrowUp, Logout } from '@mui/icons-material';
import { useAuthContext } from 'context/auth.context';

const settings: string[] = [];

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme }) => ({
  backgroundColor: theme.colors.black,
  zIndex: theme.zIndex.drawer + 1,
  position: 'fixed',
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
}));

const LogoWrapper = styled('div')`
  width: 15.625rem;
  font-size: 1.5rem;
  padding-left: 1rem;

  ${({ theme }) => theme.breakpoints.down('md')} {
    padding-left: 0;
    width: auto;
  }
`;

const UserDropdown = styled(Menu)(({ theme }) => ({
  marginTop: '3rem',
  '& .MuiPaper-root': {
    backgroundColor: theme.colors.neutral700,
    color: theme.colors.white,
  },
  '& .MuiMenuItem-root': {
    '& .MuiSvgIcon-root': {
      fontSize: '1rem',
      color: theme.colors.white,
      marginRight: '0.625rem',
    },
    '&:hover': {
      backgroundColor: theme.colors.dropShadow,
    },
  },
  '& .MuiListItemText-root': {
    '&:hover': { color: theme.colors.white },
  },
}));

const Topnav = (): JSX.Element => {
  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(null);
  const { md, showSidebar, toggleShowSidebar } = useDashboardContext();
  const [userMenuOpen, setUserMenuOpen] = useState<boolean>(false);
  const { logout, user } = useAuthContext();

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
    setUserMenuOpen(true);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
    setUserMenuOpen(false);
  };

  return (
    <>
      <AppBar position='absolute' open={showSidebar}>
        <Toolbar
          sx={{
            pr: '24px', // keep right padding when drawer closed
          }}
        >
          <IconButton
            edge='start'
            color='inherit'
            aria-label='open drawer'
            onClick={toggleShowSidebar}
            sx={{
              marginRight: '0.5rem',
              display: { md: 'none' },
              marginLeft: '0.5rem',
            }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
            <Typography
              component='h1'
              variant='h6'
              color='inherit'
              noWrap
              sx={{ ...(!md && { textAlign: 'center' }) }}
            >
              <LogoWrapper>
                <Link
                  to='/campaigns'
                  style={{
                    textDecoration: 'none',
                    color: 'inherit',
                  }}
                >
                  <Weighted $fontWeight={300}>Re</Weighted>Store
                </Link>
              </LogoWrapper>
            </Typography>
          </Box>

          {md && (
            <Box sx={{ flexGrow: 0, ml: 1 }}>
              <Box
                onClick={handleOpenUserMenu}
                sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
              >
                <IconButton sx={{ p: 0 }}>
                  <Avatar
                    sx={{
                      bgcolor: (theme) => theme.colors.blue300,
                      color: 'black',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      width: '2.1875rem',
                      height: '2.1875rem',
                    }}
                  >
                    {user?.user.firstName[0]}
                    {user?.user.lastName[0]}
                  </Avatar>
                </IconButton>
                {userMenuOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
              </Box>
              <UserDropdown
                anchorEl={anchorElUser}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorElUser)}
                disableScrollLock={true}
                onClose={handleCloseUserMenu}
              >
                {settings?.map((setting) => (
                  <MenuItem key={setting} onClick={handleCloseUserMenu}>
                    <ListItemText primary={setting} />
                  </MenuItem>
                ))}
                <MenuItem
                  onClick={() => logout()}
                  // sx={{ mt: 1.25 }} // Not necessary if while settings array is empty
                >
                  <ListItemIcon>
                    <Logout />
                  </ListItemIcon>
                  <ListItemText primary={'Log Out'} />
                </MenuItem>
              </UserDropdown>
            </Box>
          )}
        </Toolbar>
      </AppBar>
    </>
  );
};

export default Topnav;
